import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import useTriage from "../../data/useTriage";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import Modal from "../../layouts/Modal";
import { ICreateUpdateTriageStepRequest, ITriageStep } from "../../models/Triage";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import SwitchInput from "../shared/Inputs/SwitchInput";
import { TextInput } from "../shared/Inputs/TextInput";
import { SystemStepFieldPicker, systemStepOptionsString, useSystemStepField } from "../shared/SystemStepField";

type FormValues = {
  name: string,
  subtitle: string,
  order: number,
  needsValue: boolean,
  options: string,
  isActive: boolean,
};

interface TriageStepModalProps {
  open: boolean,
  setOpen: React.Dispatch<React.SetStateAction<boolean>>,
  triageStep?: ITriageStep;
  defaultOrder: number;
}

export default function TriageStepModal({ open, setOpen, triageStep, defaultOrder }: TriageStepModalProps) {
  const { t } = useTranslation("vehicles");
  const { saveHandlers } = useDefaultCRUDHandlers(t("Triage Step"));
  const { deleteHandlers } = useDefaultCRUDHandlers(t("Triage Step"));
  const { createTriageStep, updateTriageStep, deleteTriageStep } = useTriage();
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const { register, handleSubmit, reset, setValue, watch } = useForm<FormValues>();
  // Set when this is a system step - its answer is written onto the vehicle/lead when it's ticked off.
  const [systemField, setSystemField] = useState<string | null>(null);
  const { field: systemStepField } = useSystemStepField(systemField);

  const onSubmit = (data: FormValues) => {
    const updatedTriageStep: ICreateUpdateTriageStepRequest = {
      ...triageStep,
      name: data.name,
      subtitle: data.subtitle?.trim() || null,
      order: Number(data.order),
      // A system step's answers are fixed by its field; the API enforces the same.
      needsValue: systemStepField ? true : data.needsValue,
      options: systemStepField ? systemStepOptionsString(systemStepField) : data.options?.trim() || null,
      systemField,
      isActive: data.isActive,
    };

    if (triageStep !== undefined) {
      updateTriageStep.mutate(updatedTriageStep, {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setOpen(false);
          reset({});
        },
        onError: saveHandlers.onError
      });
    } else {
      createTriageStep.mutate(updatedTriageStep, {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setOpen(false);
          reset({});
        },
        onError: saveHandlers.onError
      });
    }
  };

  const handleDelete = () => {
    deleteTriageStep.mutate(triageStep?.id!, {
      onSuccess: () => {
        deleteHandlers.onSuccess();
        setOpen(false);
        setDeleteOpen(false);
        reset({});
      },
      onError: deleteHandlers.onError
    });
  };

  useEffect(() => {
    if (triageStep !== undefined) {
      reset({
        name: triageStep.name,
        subtitle: triageStep.subtitle ?? "",
        order: triageStep.order,
        needsValue: triageStep.needsValue,
        options: triageStep.options ?? "",
        isActive: triageStep.isActive,
      });
    } else {
      reset({
        name: "",
        subtitle: "",
        order: defaultOrder,
        needsValue: false,
        options: "",
        isActive: true,
      });
    }
    setSystemField(triageStep?.systemField ?? null);
  }, [triageStep, open, reset, defaultOrder]);

  return (
    <>
      <ConfirmModal open={deleteOpen} setOpen={setDeleteOpen}
        title={t("Delete Triage Step")} message={t("Are you sure you want to delete this triage step? This action cannot be undone.")}
        confirmButtonText={t("Delete")} onConfirm={handleDelete} isLoading={deleteTriageStep.isLoading}
      />
      <Modal width="max-w-2xl" open={open} setOpen={setOpen} >
        <h1 className="text-xl">{triageStep ? t("Edit a Triage Step") : t("Add a Triage Step")}</h1>
        <h1 className="text-sm text-gray-500">{t("All triage steps will need to be ticked off but can optionally return a value.")}</h1>
        <div className="mt-4">
          <form className="space-y-4 border-t border-gray-200" onSubmit={handleSubmit(onSubmit)} >
            <div className="pt-4 grid grid-cols-1 gap-x-4 gap-y-4 sm:gap-y-6 sm:grid-cols-1">

              <SystemStepFieldPicker
                value={systemField}
                onChange={(field) => {
                  setSystemField(field?.key ?? null);
                  if (field && !watch("name")?.trim()) setValue("name", t(field.label));
                }}
              />

              <TextInput label={t("Name")} registerName="name" register={register} required={true} />
              <TextInput label={t("Subtitle")} registerName="subtitle" register={register} />
              <input type="hidden" {...register("order")} />

              <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                {!systemField && (
                  <SwitchInput label={t("Needs Value")} registerName="needsValue" register={register} setValue={setValue} watch={watch} />
                )}
                <SwitchInput label={t("Is Active")} registerName="isActive" register={register} setValue={setValue} watch={watch} />
              </div>

              {!systemField && (
                <>
                  <TextInput
                    label={t("Options")}
                    registerName="options"
                    register={register}
                    placeholder={t("Alloy,Steel,Other")}
                  />
                  <p className="text-sm text-gray-500">
                    {t("Leave options blank for free text, or enter comma-separated values for a dropdown.")}
                  </p>
                </>
              )}

              <ButtonInput
                label={triageStep ? t("Save Changes") : t("Create")}
                isSubmit={true}
                onClick={undefined}
                classes={undefined}
                isLoading={createTriageStep.isLoading || updateTriageStep.isLoading}
              />
              {triageStep !== undefined && (
                <ButtonInput label={t("Delete")} isSubmit={false} onClick={() => (setDeleteOpen(true))} classes="!bg-red-500 hover:!bg-red-600" />
              )}
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
}
